import { Icon } from '@acpaas-ui/react-components';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import RichTextEditor from 'react-rte';
import { INPUT_STATES } from './forms.utils';

const TOOLBAR_CONFIG = {
  display: ['INLINE_STYLE_BUTTONS', 'BLOCK_TYPE_BUTTONS', 'HISTORY_BUTTONS'],
  INLINE_STYLE_BUTTONS: [
    { label: 'Bold', style: 'BOLD', className: 'custom-css-class' },
    { label: 'Italic', style: 'ITALIC' },
    { label: 'Underline', style: 'UNDERLINE' }
  ],
  BLOCK_TYPE_BUTTONS: [
    { label: 'UL', style: 'unordered-list-item' },
    { label: 'OL', style: 'ordered-list-item' }
  ],
  extraProps: { tabIndex: -1 }
};

const RichTextArea = ({
  label,
  name,
  className = '',
  required,
  disabled,
  placeholder,
  value = '',
  state = INPUT_STATES.DEFAULT,
  hasError = false,
  onChange,
  onSubmit,
  onCancel = () => {},
  onEditStart = () => {},
  readOnly = false,
  isLoading,
  isSubmitting,
  tabIndex,
  showAsText,
  description = '',
  validationError = ''
}) => {
  const editorRef = useRef(null);
  const [editorValue, setEditorValue] = useState(RichTextEditor.createValueFromString(value, 'html'));
  const [isFocused, setIsFocused] = useState(false);
  const isRead = state === INPUT_STATES.READ;
  const isDefault = state === INPUT_STATES.DEFAULT;

  useEffect(() => {
    if (isLoading || !isFocused) {
      setEditorValue(RichTextEditor.createValueFromString(value, 'html'));
    }
    // KS: its intentional, we want to listen only on isLoading change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, isLoading]);

  const handleEditStart = (e) => {
    if (isDefault) {
      setIsFocused(true);
    }
    if (!readOnly && !disabled && isRead) {
      setIsFocused(true);
      e.stopPropagation();
      onEditStart();
    }
  };

  const handleBlur = () => {
    if (isDefault) {
      setIsFocused(false);
    }
  };

  const handleCancel = () => {
    onCancel();
    setIsFocused(false);
  };

  const handleSubmit = () => {
    onSubmit?.(null, (success) => success && setIsFocused(false));
  };

  const handleChange = (event) => {
    setEditorValue(event);

    onChange && onChange(event.toString('html'), name);
  };

  return (
    <div
      className={`spo-a-rich-text-area ${state} a-input ${className} ${required ? 'is-required' : ''} ${
        disabled ? 'is-disabled' : ''
      } ${isFocused ? 'is-focused' : ''}`}
    >
      {label && (
        <label className={`a-input__label`} htmlFor={name}>
          {label}
        </label>
      )}
      {showAsText ? (
        <div dangerouslySetInnerHTML={{ __html: value }} className="spo-a-readfield spo-a-readfield--max-height"></div>
      ) : (
        <div
          // tabIndex={tabIndex}
          className="spo-a-rich-text-area__focus-detector u-full-height"
          onFocus={handleEditStart}
          onBlur={handleBlur}
        >
          <RichTextEditor
            ref={editorRef}
            className={`spo-a-rich-text-area__editor-wrapper ${className} ${disabled ? 'is-disabled' : ''}`}
            editorClassName={`spo-a-rich-text-area__editor u-full-height ${
              !disabled ? 'spo-a-rich-text-area__editor--enabled' : ''
            }`}
            toolbarClassName="spo-a-rich-text-area__toolbar"
            value={editorValue}
            name={name}
            id={name}
            onChange={handleChange}
            toolbarConfig={TOOLBAR_CONFIG}
            placeholder={placeholder}
            disabled={disabled}
            tabIndex={tabIndex}
          />
          {isSubmitting && <span className="a-spinner a-spinner--sm spinner ai"></span>}
        </div>
      )}
      {(description || validationError) && (
        <small
          className={`${hasError || validationError ? 'u-text-danger' : ''} description`}
          id="text-field-description"
        >
          {validationError || description}
        </small>
      )}
      <div className={`actions ${isRead || isDefault ? 'hidden' : ''}`}>
        {/* ACPaaS Button doest support setting tab index */}
        <button
          className="a-button a-button--success a-button--small has-icon"
          aria-label="Submit"
          disabled={!onSubmit || isSubmitting || disabled}
          onClick={handleSubmit}
          tabIndex={isRead || isDefault ? '-1' : ''}
        >
          <Icon name="ai-check-1" />
        </button>
        <button
          className="a-button a-button--danger a-button--small a-button-outline has-icon"
          aria-label="Cancel"
          disabled={isSubmitting || disabled}
          onClick={handleCancel}
          tabIndex={isRead || isDefault ? '-1' : ''}
        >
          <Icon name="ai-close" />
        </button>
      </div>
    </div>
  );
};
RichTextArea.propTypes = {
  isLoading: PropTypes.bool,
  label: PropTypes.string,
  name: PropTypes.string,
  className: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func
};

export default RichTextArea;
